import { MouseEvent, useEffect, useRef, useState } from 'react';

import {
  useIsAllowedPermission,
  usePermissions,
} from '@mint-lib/profile-helpers';

import {
  BUILDER_ROW_TYPES,
  DECIMAL_LIMIT,
} from '../../../../../../constants/campaignBuilder.js';
import { useCellValueUpdate } from '../../../../../../hooks/useCellValueUpdate.js';
import { TRACKING_EVENTS } from '../../../../../../types/performanceTracking.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';

type UseEditableFieldArgs = {
  value: string | null;
  row: BuilderTableRow;
  column: BuilderTableColumn;
};

const getNumberValue = (value: string | number | null) => {
  const numberValue = Number(value);

  if ((!value && value !== 0) || value === '-' || Number.isNaN(numberValue)) {
    return null;
  }

  return numberValue;
};

const useEditableNumericField = ({
  value,
  row,
  column,
}: UseEditableFieldArgs) => {
  const [isFieldFocused, setIsFieldFocused] = useState<boolean>(false);
  const [numberValue, setNumberValue] = useState<number | null>(
    getNumberValue(value),
  );

  const { updateCellValue } = useCellValueUpdate({ column, row });
  const numberValueRef = useRef<string | number | null>(getNumberValue(value));

  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const { getPermissionField } = usePermissions();
  const isNonFinancialUser = !getPermissionField('financialDataFeature');

  const { startMeasurePerformance, decimals, isCalculatedTechCostColumn } =
    column;
  const {
    isCampaignCurrencyMode,
    isBuyingPlanLocked,
    type,
    isPredictivePlanningMode,
    isArchivedVersion,
  } = row.original;

  const decimalLimit = decimals ?? DECIMAL_LIMIT.INTEGER;
  const isTileRow = type === BUILDER_ROW_TYPES.TILE;

  const isEditModeAvailable =
    isAllowed &&
    !isNonFinancialUser &&
    !isCampaignCurrencyMode &&
    !isBuyingPlanLocked;
  const isReadOnlyField =
    !isTileRow ||
    !isAllowed ||
    isPredictivePlanningMode ||
    isArchivedVersion ||
    isCampaignCurrencyMode ||
    isBuyingPlanLocked;

  const handleFieldFocus = () => {
    if (!isEditModeAvailable) {
      return;
    }
    setIsFieldFocused(true);
  };

  const handleUpdateValue = (newValue: string | number | null) => {
    setNumberValue(getNumberValue(newValue));
    numberValueRef.current = newValue;
    setIsFieldFocused(false);
  };

  const handleFieldBlur = async () => {
    //TODO: weak logic, need to be refactored and covered with tests
    const savedValue = getNumberValue(value);
    const currentValue = getNumberValue(numberValueRef.current);

    if (currentValue !== savedValue) {
      startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);

      const isValueExist = currentValue || currentValue === 0;
      const fixedDecimalNumber = Number(currentValue).toFixed(decimalLimit);

      const preparedValueForSending = isValueExist ? fixedDecimalNumber : null;

      const newValue = await updateCellValue({
        currentValue: savedValue,
        newValue: getNumberValue(preparedValueForSending),
      });

      handleUpdateValue(newValue);
      return;
    }
    handleUpdateValue(savedValue);
  };

  const handleValueChange = (newValue: number | null) => {
    numberValueRef.current = newValue;
    setNumberValue(newValue);
  };

  const handleValueReset = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);

    const newValue = isCalculatedTechCostColumn ? 0 : null;

    await updateCellValue({
      currentValue: value,
      newValue: isCalculatedTechCostColumn ? '0.00' : null,
    });

    numberValueRef.current = newValue;
    handleUpdateValue(newValue);
  };

  const handleKeyDown = (e: globalThis.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleFieldBlur();
    }
  };

  useEffect(() => {
    if (isFieldFocused) {
      globalThis.addEventListener('keydown', handleKeyDown);

      return () => {
        globalThis.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isFieldFocused]);

  useEffect(() => {
    setNumberValue(getNumberValue(value));
  }, [value]);

  return {
    isFieldFocused,
    handleFieldFocus,
    handleFieldBlur,
    isReadOnlyField,
    numberValue,
    handleValueChange,
    handleValueReset,
  };
};

export default useEditableNumericField;
